import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';

interface ITheme {
  name: string,
  baseColor?: string,
  isActive?: boolean
}

/** Maps a backend role name to the CSS class applied on <body> and its priority. */
interface IRoleTheme {
  role: string;
  cssClass: string;
  priority: number;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly roleThemes: ReadonlyArray<IRoleTheme> = [
    { role: 'ClientSystemAdmin', cssClass: 'role-nhs-clients', priority: 10 },
    { role: 'ClinicalTeamAdmin', cssClass: 'role-nhs-clients', priority: 10 },
    { role: 'SeniorManagement', cssClass: 'role-nhs-clients', priority: 10 },
    { role: 'ClinicalServiceLead', cssClass: 'role-nhs-clients', priority: 10 },
    { role: 'Clinician', cssClass: 'role-nhs-clients', priority: 10 },
  ];

  private readonly userObjectKey = 'userObject';
  private readonly roleThemeProp = 'activeRoleClass';
  private readonly internalRenderer: Renderer2;

  public egretThemes: ITheme[] = [{ name: 'ebo-theme', baseColor: '#009688', isActive: true }];
  public activatedTheme: ITheme;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2
  ) {
    this.internalRenderer = rendererFactory.createRenderer(null, null);
  }

  applyMatTheme(r: Renderer2) {
    this.activatedTheme = this.egretThemes[0];
    this.changeTheme(r, this.activatedTheme);
  }

  changeTheme(r: Renderer2, theme: ITheme) {
    r.removeClass(this.document.body, this.activatedTheme.name);
    r.addClass(this.document.body, theme.name);
    this.flipActiveFlag(theme);
  }

  flipActiveFlag(theme: ITheme) {
    this.egretThemes.forEach((t) => {
      t.isActive = false;
      if (t.name === theme.name) {
        t.isActive = true;
        this.activatedTheme = theme;
      }
    });
  }

  /**
   * Reads the cached active role CSS class from sessionStorage and applies it
   * synchronously — no API call. Call this in AppComponent.ngAfterViewInit.
   */
  applyThemeFromCache(): void {
    const userObject = JSON.parse(sessionStorage.getItem(this.userObjectKey));
    const cached: string | undefined = userObject?.[this.roleThemeProp];
    if (cached !== undefined) {
      this.applyRoleClassToDOM(cached || null);
    }
  }

  /**
   * Applies the role theme directly from an already-fetched roles array.
   * Called by the NgRx effect after loading roles from the API.
   */
  applyRoleThemeFromRoles(roles: string[]): void {
    this.applyRoleClass(this.resolveRoleClass(roles));
  }

  private resolveRoleClass(userRoles: string[]): string | null {
    const matched = [...this.roleThemes]
      .filter(t => userRoles.includes(t.role))
      .sort((a, b) => b.priority - a.priority)[0];
    return matched?.cssClass ?? null;
  }

  private applyRoleClass(cssClass: string | null): void {
    this.applyRoleClassToDOM(cssClass);
    const userObject = JSON.parse(sessionStorage.getItem(this.userObjectKey)) ?? {};
    userObject[this.roleThemeProp] = cssClass ?? '';
    sessionStorage.setItem(this.userObjectKey, JSON.stringify(userObject));
  }

  private applyRoleClassToDOM(cssClass: string | null): void {
    this.roleThemes.forEach(t => {
      this.internalRenderer.removeClass(this.document.body, t.cssClass);
    });
    if (cssClass) {
      this.internalRenderer.addClass(this.document.body, cssClass);
    }
  }
}
